import React from 'react';
import { Users, Shirt, Building, Star, Trophy, Target, CheckCircle, ArrowRight, Zap, Shield, Award } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';

const Services = () => {
  const services = [
    {
      icon: Users,
      title: "Adaptive Gear Collabs",
      description: "Partner with us to develop specialized equipment and apparel for your adaptive sports program.",
      features: ["Co-design process", "Community feedback", "Prototype testing", "Revenue sharing"],
      price: "Custom pricing",
      highlight: "Innovation Focus",
      color: "purple"
    },
    {
      icon: Shirt,
      title: "Custom Team Kits",
      description: "Tailored team gear that adapts to each player's unique needs while maintaining team unity and style.",
      features: ["Team branding", "Custom designs", "Revenue sharing", "Group discounts"],
      price: "Custom pricing per team",
      highlight: "Most Popular",
      color: "lime"
    },
    {
      icon: Building,
      title: "Corporate Clinics",
      description: "Inclusive pickleball clinics and team-building events that bring adaptive sports to your workplace.",
      features: ["On-site instruction", "Equipment provided", "Inclusion training", "Team building"],
      price: "Starting at $500/session",
      highlight: "Team Building",
      color: "orange"
    }
  ];

  const testimonials = [
    {
      quote: "PBX Pickle transformed our team's identity. The custom kits perfectly balance our individual needs with team unity.",
      author: "Sarah Martinez",
      role: "Team Captain, Phoenix Picklers",
      rating: 5
    },
    {
      quote: "Their adaptive gear collaboration helped us create equipment that's now used nationwide. Game-changing partnership.",
      author: "Dr. James Chen",
      role: "Adaptive Sports Director",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      <Navigation />
      
      {/* Integrated Hero Section with Services */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-lime-400/5 via-transparent to-purple-600/5" />
          <div className="absolute top-20 left-10 w-32 h-32 bg-lime-400/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-purple-600/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero Content */}
          <div className="text-center mb-20">
            <div className="inline-block bg-lime-400/10 border border-lime-400/20 rounded-full px-6 py-2 mb-6">
              <span className="text-lime-400 font-bold text-sm uppercase tracking-wide">
                ⚡ Elevating Teams & Athletes
              </span>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-black text-white mb-8 tracking-tight leading-none">
              SERVICES THAT{' '}
              <span className="text-lime-400 block sm:inline">SERVE</span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Beyond apparel - we're building an inclusive ecosystem where every athlete, team, and organization can thrive with purpose-driven solutions.
            </p>
          </div>

          {/* Services Grid - Integrated in Hero */}
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <Card 
                key={index}
                className={`relative bg-gradient-to-br ${
                  service.color === 'lime' ? 'from-lime-400/15 to-lime-400/5 border-lime-400/30' :
                  service.color === 'purple' ? 'from-purple-600/15 to-purple-600/5 border-purple-600/30' :
                  'from-orange-500/15 to-orange-500/5 border-orange-500/30'
                } hover:scale-105 transition-all duration-500 group overflow-hidden ${
                  service.color === 'lime' ? 'lg:scale-110 lg:z-10' : ''
                }`}
              >
                {/* Highlight Badge */}
                <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-xs font-bold ${
                  service.color === 'lime' ? 'bg-lime-400 text-black' :
                  service.color === 'purple' ? 'bg-purple-600 text-white' :
                  'bg-orange-500 text-white'
                }`}>
                  {service.highlight}
                </div>

                {/* Glow Effect */}
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                  service.color === 'lime' ? 'bg-lime-400/5' :
                  service.color === 'purple' ? 'bg-purple-600/5' :
                  'bg-orange-500/5'
                }`} />

                <CardContent className="relative p-8">
                  {/* Enhanced Icon */}
                  <div className={`relative mb-8 ${
                    service.color === 'lime' ? 'bg-lime-400' :
                    service.color === 'purple' ? 'bg-purple-600' :
                    'bg-orange-500'
                  } rounded-2xl w-20 h-20 flex items-center justify-center group-hover:scale-110 transition-all duration-300`}>
                    <service.icon className="w-10 h-10 text-white" />
                    <div className={`absolute inset-0 rounded-2xl ${
                      service.color === 'lime' ? 'bg-lime-400' :
                      service.color === 'purple' ? 'bg-purple-600' :
                      'bg-orange-500'
                    } opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300`} />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl lg:text-3xl font-black text-white mb-4 uppercase tracking-tight">
                    {service.title}
                  </h3>

                  {/* Enhanced Description */}
                  <p className="text-gray-300 mb-8 leading-relaxed text-lg">
                    {service.description}
                  </p>

                  {/* Features with Better Styling */}
                  <div className="space-y-4 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center text-gray-300">
                        <CheckCircle className={`w-5 h-5 mr-3 ${
                          service.color === 'lime' ? 'text-lime-400' :
                          service.color === 'purple' ? 'text-purple-400' :
                          'text-orange-400'
                        }`} />
                        <span className="font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Enhanced Price & CTA */}
                  <div className="border-t border-gray-700/50 pt-8">
                    <div className={`font-bold text-xl mb-6 ${
                      service.color === 'lime' ? 'text-lime-400' :
                      service.color === 'purple' ? 'text-purple-400' :
                      'text-orange-400'
                    }`}>
                      {service.price}
                    </div>
                    <Button className={`w-full font-black py-4 text-lg rounded-xl transition-all duration-300 hover:scale-105 group/btn ${
                      service.color === 'lime' ? 'bg-lime-400 text-black hover:bg-lime-300 hover:shadow-xl hover:shadow-lime-400/25' :
                      service.color === 'purple' ? 'bg-purple-600 text-white hover:bg-purple-500 hover:shadow-xl hover:shadow-purple-600/25' :
                      'bg-orange-500 text-white hover:bg-orange-400 hover:shadow-xl hover:shadow-orange-500/25'
                    }`}>
                      GET STARTED TODAY
                      <ArrowRight className="ml-2 w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-r from-lime-400/5 to-purple-600/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-black text-white mb-6 uppercase tracking-tight">
              Success Stories
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Real results from teams and organizations we've partnered with.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-gradient-to-br from-lime-400/10 to-purple-600/10 border-lime-400/20 hover:border-lime-400/40 transition-all duration-300">
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-lime-400 fill-current" />
                    ))}
                  </div>
                  <blockquote className="text-lg text-gray-300 mb-6 leading-relaxed">
                    "{testimonial.quote}"
                  </blockquote>
                  <div>
                    <div className="font-bold text-white">{testimonial.author}</div>
                    <div className="text-lime-400 text-sm">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-lime-400/10 via-purple-600/10 to-lime-400/10 border border-lime-400/20 rounded-3xl p-12 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="flex justify-center mb-6">
                <div className="bg-lime-400 rounded-full p-4">
                  <Zap className="w-8 h-8 text-black" />
                </div>
              </div>
              
              <h2 className="text-4xl lg:text-5xl font-black text-white mb-6 uppercase tracking-tight">
                Ready to Transform Your Team?
              </h2>
              
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Join 200+ teams already making an impact. Let's create something extraordinary together.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="bg-lime-400 text-black hover:bg-lime-300 font-black px-10 py-5 text-lg rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-lime-400/25">
                  START YOUR PROJECT
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button 
                  variant="outline" 
                  className="border-lime-400 text-lime-400 hover:bg-lime-400 hover:text-black font-bold px-10 py-5 text-lg rounded-xl transition-all duration-300"
                >
                  SCHEDULE CONSULTATION
                </Button>
              </div>
              
              <p className="text-gray-400 text-sm mt-4">
                ⚡ Free consultation • Custom quote in 24hrs • No commitment required
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Services;
